import React, { useState } from 'react'
import { FIELD_NAMES } from 'const/forms'
import useAction from 'utils/useAction'
import {
  openConfirmModal,
  openUserModal,
  openMergeUserModal,
  openResetPasswordModal,
  openSMSVerificationModal,
  openUserWalletHistoryModal,
  openEarnOffersModal,
} from 'reducers/modals/actions'
import {
  confirmPayPal,
  deleteUser,
  fetchUsers,
  sendSMSVerification,
  unsubscribeUser,
} from 'actions/views/users'
import { Button, Dropdown, Input, Menu, Modal, message } from 'antd'
import { MoreOutlined } from '@ant-design/icons'
import MenuItem from 'antd/es/menu/MenuItem'
import { apiInstance3 } from 'utils/request'

const UserActions = ({ data }) => {
  const isPayPalConfirmationAvailable =
    data[FIELD_NAMES.paypalConnected] && !data[FIELD_NAMES.paypalConfirmed]

  const deleteUserFunc = useAction(deleteUser)
  const sendSMSVerificationFunc = useAction(sendSMSVerification)
  const unsubscribeUserFunc = useAction(unsubscribeUser)
  const confirmPayPalFunc = useAction(confirmPayPal)
  const fetchUsersFunc = useAction(fetchUsers)

  const isSuspended = data[FIELD_NAMES.cashbackStatus] === 'suspended'

  const [banReasonModalVisible, setBanReasonModalVisible] = useState(false)
  const [banReason, setBanReason] = useState('')
  const [banLoading, setBanLoading] = useState(false)

  const openUserModalFunc = useAction(openUserModal)
  const openConfirmModalFunc = useAction(openConfirmModal)
  const openMergeUserModalFunc = useAction(openMergeUserModal)
  const openResetPasswordFunc = useAction(openResetPasswordModal)
  const openSMSVerificationModalFunc = useAction(openSMSVerificationModal)
  const openUserWalletHistoryModalFunc = useAction(openUserWalletHistoryModal)
  const openEarnOffersModalFunc = useAction(openEarnOffersModal)

  const handleEditUser = () => openUserModalFunc(data)
  const handleMergeUser = () => openMergeUserModalFunc(data)

  const handleBlockWithdrawal = () => {
    setBanReason('')
    setBanReasonModalVisible(true)
  }

  const handleCreateBan = () => {
    if (!banReason.trim()) {
      message.warning('Please enter a ban reason.')
      return
    }
    setBanLoading(true)
    apiInstance3
      .post(`/backoffice/users/${data[FIELD_NAMES.id]}/toggle_cashback_ban`, {
        reason: banReason,
      })
      .then(() => {
        message.success('Withdrawal blocked successfully.')
        fetchUsersFunc()
      })
      .catch((err) => {
        if (err.toString().includes('422'))
          message.error(
            'Ban for this user already exists or no user with this id.'
          )
        else message.error('Failed to block withdrawal.')
      })
      .finally(() => {
        setBanLoading(false)
        setBanReasonModalVisible(false)
        setBanReason('')
      })
  }

  const handleUnblockWithdrawal = () => {
    const text = `Withdrawal is currently BLOCKED for user ${
      data[FIELD_NAMES.name]
    } (ID: ${data[FIELD_NAMES.id]}). Do you want to unblock it?`
    const callback = () => {
      apiInstance3
        .post(`/backoffice/users/${data[FIELD_NAMES.id]}/toggle_cashback_ban`)
        .then((res) => {
          const { earnings_resumed, earnings_failed } = res.data
          let msg = 'Withdrawal unblocked successfully.'
          if (earnings_resumed) msg += ` Earnings resumed: ${earnings_resumed}.`
          if (earnings_failed) msg += ` Earnings failed: ${earnings_failed}.`
          message.success(msg)
          fetchUsersFunc()
        })
        .catch(() => {
          message.error('Failed to unblock withdrawal.')
        })
    }
    openConfirmModalFunc({ callback, text })
  }

  const handleToggleWithdrawalBlocked = () => {
    if (isSuspended) {
      handleUnblockWithdrawal()
    } else {
      handleBlockWithdrawal()
    }
  }

  const handleResetPassword = () => openResetPasswordFunc(data)

  const handleConfirmPayPal = () => {
    const callback = () => confirmPayPalFunc(data[FIELD_NAMES.id])
    const text =
      `Are you sure you want confirm PayPal for the user ${
        data[FIELD_NAMES.name]
      } ` + `(ID: ${data[FIELD_NAMES.id]})? This action can't be revoked.`
    openConfirmModalFunc({ callback, text })
  }

  const handleSMSVerification = () => {
    sendSMSVerificationFunc(data[FIELD_NAMES.id])
    openSMSVerificationModalFunc(data)
  }

  const handleWalletHistory = () => {
    openUserWalletHistoryModalFunc({ userId: data[FIELD_NAMES.id] })
  }

  const handleEarnOffers = () => {
    openEarnOffersModalFunc({ userId: data[FIELD_NAMES.id] })
  }

  const handleDeleteUser = () => {
    const text =
      `You are about to delete user with id: ${data[FIELD_NAMES.id]}` +
      `${
        data[FIELD_NAMES.name]
          ? ', name: ' + data[FIELD_NAMES.name] + '. '
          : '. '
      }` +
      'If you are sure, please type the user\'s email.'
    const text2 = `The user with email ${
      data[FIELD_NAMES.email]
    } will be deleted permanently. Do you want to proceed?`
    const callback2 = () => deleteUserFunc(data[FIELD_NAMES.id])
    const callback = () =>
      openConfirmModalFunc({ callback: callback2, text: text2 })
    openConfirmModalFunc({
      callback,
      text,
      inputToConfirm: data[FIELD_NAMES.email],
      textForConfirmation: "user's email",
      buttonText: 'Delete',
      isCopyAndPasteDisabled: true,
    })
  }
  const handleUnsubscribe = () => {
    const callback = () => unsubscribeUserFunc(data[FIELD_NAMES.id])
    const text = `Are you sure you want to unsubscribe ${
      data[FIELD_NAMES.name]
    } (ID: ${data[FIELD_NAMES.id]}) from all alerts? `
    openConfirmModalFunc({ callback, text })
  }

  const menu = (
    <Menu>
      <MenuItem onClick={handleEditUser}>Edit</MenuItem>
      <MenuItem onClick={handleMergeUser}>Merge</MenuItem>
      <MenuItem onClick={handleToggleWithdrawalBlocked}>
        {isSuspended ? 'Unblock Withdrawal' : 'Block Withdrawal'}
      </MenuItem>
      <MenuItem onClick={handleUnsubscribe}>Unsubscribe All Alerts</MenuItem>
      <MenuItem onClick={handleResetPassword}>Reset Password</MenuItem>
      {isPayPalConfirmationAvailable && (
        <MenuItem onClick={handleConfirmPayPal}>Confirm PayPal</MenuItem>
      )}
      <MenuItem onClick={handleSMSVerification}>SMS Verification</MenuItem>
      <MenuItem onClick={handleWalletHistory}>Wallet History</MenuItem>
      <MenuItem onClick={handleEarnOffers}>Earn Offers</MenuItem>
      <MenuItem onClick={handleDeleteUser} danger>
        Delete
      </MenuItem>
    </Menu>
  )

  return (
    <>
      <Dropdown overlay={menu} trigger={['click']}>
        <Button icon={<MoreOutlined />} shape="circle" size="small" />
      </Dropdown>
      <Modal
        title="Block Withdrawal"
        open={banReasonModalVisible}
        onOk={handleCreateBan}
        onCancel={() => setBanReasonModalVisible(false)}
        confirmLoading={banLoading}
        okText="Block"
      >
        <p>
          Block withdrawal for user {data[FIELD_NAMES.name]} (ID:{' '}
          {data[FIELD_NAMES.id]})?
        </p>
        <Input.TextArea
          placeholder="Please enter ban reason..."
          value={banReason}
          onChange={(e) => setBanReason(e.target.value)}
          rows={3}
        />
      </Modal>
    </>
  )
}

export default UserActions
